import { useState } from "react";
import { CalendarRange, Gauge } from "lucide-react";
import { Button, SegmentedControl, Skeleton } from "@nexus/ui";
import {
  Widget,
  firstError,
  resolveLabels,
  unwrap,
  type JsonValue,
  type WidgetManifest,
  type WidgetProps,
  WidgetIssueState,
} from "@nexus/widget-sdk";

import { isOngoing, parseDbTime, useNow } from "@nexus/widget-kit";
import { LABELS } from "./labels";
import type { LiveMonitoringData, MonitoredEvent } from "./model";
import { QuietState, SituationStrip } from "./Overview";
import { PerformanceView } from "./PerformanceView";
import { ScheduleView } from "./ScheduleView";

export { renderDetail } from "./detail";
export { summarizeSegment } from "./model";
export { parseDbTime as eventTime } from "@nexus/widget-kit";
export type { MonitoredEvent, LiveMonitoringData } from "./model";
export { LABELS } from "./labels";

export const manifest: WidgetManifest = {
  id: "event-timeline-advanced",
  name: "Live Event Monitor",
  version: "1.0.0",
  description:
    "Live event performance, detection targets and operational exceptions across protected channels.",
  dataRequests: {
    events: {
      source: "event-timeline-advanced",
      contract: { name: "event-timeline-advanced", version: 1 },
      configurable: false,
    },
  },
  labels: LABELS,
};
export const defaultConfig = { title: "Live event monitoring" };

type Mode = "performance" | "timeline";

/**
 * The live control point of the dashboard, read top-down: the situation
 * (live count, attention, next event), then the events ranked by what needs
 * attention, then — on click — the event's detail panel. Timeline shows the
 * same events in the programme.
 */
export function Component({ data, config, dashboard }: WidgetProps<typeof defaultConfig>) {
  const labels = resolveLabels(LABELS, config);
  const resolved = unwrap<LiveMonitoringData>(data.events);
  const error = firstError(data, ["events"]);
  const now = useNow(30000);
  const [mode, setMode] = useState<Mode>("performance");

  const events = resolved?.events ?? [];
  const live = events.filter((event) => isOngoing(event, now));
  const upcoming = events
    .filter((event) => parseDbTime(event.event_start) > now)
    .sort((a, b) => parseDbTime(a.event_start) - parseDbTime(b.event_start));
  const attention = live.filter((event) => event.unresolved_anomaly_count > 0).length;
  const openEvent = dashboard.emitInteraction
    ? (event: MonitoredEvent) =>
        dashboard.emitInteraction?.({
          type: "event.monitoring.detail",
          payload: {
            event: event as unknown as JsonValue,
            from: resolved!.from,
            to: resolved!.to,
            fetchedAt: resolved!.fetchedAt,
          },
          source: { dataKey: "events" },
        })
    : undefined;

  return (
    <Widget
      title={config.title ?? defaultConfig.title}
      actions={
        <SegmentedControl
          label="Monitoring view"
          variant="inset"
          value={mode}
          onChange={setMode}
          className="h-8 [&>button]:h-6"
          options={[
            { value: "performance", label: labels.performanceTab, icon: Gauge },
            { value: "timeline", label: labels.timelineTab, icon: CalendarRange },
          ]}
        />
      }
    >
      <div className="@container density-widget-body">
        {error ? (
          <div>
            <WidgetIssueState error={error} />
            {data.events?.retry ? (
              <Button size="sm" className="mx-auto mt-2 flex" onClick={data.events.retry}>
                {labels.retry}
              </Button>
            ) : null}
          </div>
        ) : !resolved ? (
          <MonitorSkeleton />
        ) : (
          <>
            {live.length ? (
              <SituationStrip
                live={live.length}
                attention={attention}
                next={upcoming[0] ?? null}
                now={now}
                fetchedAt={resolved.fetchedAt}
                labels={labels}
              />
            ) : null}
            {mode === "timeline" && events.length ? (
              <ScheduleView
                key={`${resolved.from}:${resolved.to}:${dashboard.filters.tenantIds?.join(",")}`}
                events={events}
                now={now}
                interactive={Boolean(openEvent)}
                onSelect={(event) => openEvent?.(event)}
              />
            ) : live.length ? (
              <PerformanceView events={live} now={now} labels={labels} onOpen={openEvent} />
            ) : (
              <QuietState upcoming={upcoming} now={now} labels={labels} onOpen={openEvent} />
            )}
          </>
        )}
      </div>
    </Widget>
  );
}

function MonitorSkeleton() {
  return (
    <div role="status" aria-busy="true" aria-label="Loading live event monitoring">
      <div className="flex items-center gap-6 pb-3" aria-hidden="true">
        <Skeleton className="h-7 w-16" />
        <Skeleton className="h-6 w-36 rounded-full" />
        <Skeleton className="h-4 w-48" />
      </div>
      <div className="border-t border-border" aria-hidden="true">
        {[0, 1, 2].map((index) => (
          <div key={index} className="flex items-center gap-6 border-b border-border/70 px-2 py-3">
            <div className="min-w-0 flex-[2]">
              <Skeleton className={index % 2 ? "h-4 w-1/2" : "h-4 w-2/3"} />
              <Skeleton className="mt-1.5 h-3 w-1/3" />
            </div>
            <Skeleton className="h-5 flex-[1.4]" />
            <Skeleton className="hidden h-4 w-12 @3xl:block" />
            <Skeleton className="hidden h-4 w-10 @3xl:block" />
            <Skeleton className="hidden h-1.5 flex-1 @3xl:block" />
          </div>
        ))}
      </div>
    </div>
  );
}
